import { LegacyRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { appAsset } from "../../app.asset";
import { QuotationFormSchema } from "../../factory/form/form.rule";
import { GetQuoteActivityModel } from "../../factory/model/query-model-quote-activity";
import { useQyGetActivity } from "../../factory/query/activity.query";
import TextBlock from "../../shared/ui/text-block/text-block";
import { useDateTimeContext } from "../../shared/utility/date-time-service/date-time.context";
import PreviewInputControl from "./preview-input-control";
import PreviewTextAreaControl from "./preview-textarea-control";
import SummaryTable from "./summary-table";

interface PreviewFileProps {
  fileRef: LegacyRef<HTMLDivElement> | undefined;
  downloading?: boolean;
}
export function PreviewFile({
  fileRef,
  downloading = false,
}: PreviewFileProps) {
  const { formatDate } = useDateTimeContext();
  const { watch, control, getValues } = useFormContext<QuotationFormSchema>();
  const [optionalActivities, setOptionalActivities] = useState<
    GetQuoteActivityModel[]
  >([]);
  const {
    fields: packageInclusions,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "packageInclusions",
  });

  const {
    fields: customRows,
    append: appendRow,
    remove: removeRow,
  } = useFieldArray({
    control,
    name: "customRows",
  });
  const formData = watch();

  const numDays = watch("numDays");
  const customDays = getValues("customDays");
  const dayOptions = numDays
    ? Array.from({ length: numDays }, (_, i) => ({
      label: `Day ${i + 1}`,
      value: i + 1,
      index: i,
    }))
    : [];

  useQyGetActivity(
    "",
    9999,
    0,
    undefined,
    { show_as_optional: "1" },
    true,
    (response) => {
      const activities = (response?.data || []).map(
        (item) =>
          ({
            name: item.name,
          }) as GetQuoteActivityModel,
      );
      setOptionalActivities(activities);
    },
  );

  return (
    <div
      ref={fileRef}
      id="preview-file"
      className="flex flex-col justify-between min-h-[1056px] bg-white"
    >
      <div className="preview-file px-10 py-2 space-y-2">
        <section className="flex items-center justify-between">
          <div>
            <h1>TRAVEL</h1>
            <h1>QUOTATION</h1>
          </div>
          <div>
            <img
              src={appAsset.previewLogo}
              alt="Preview Logo"
              className="w-full"
            />
          </div>
        </section>
        <section className="grid grid-cols-2">
          <div>
            <TextBlock label="Client Name" value={formData.clientName} />
            <TextBlock label="Destination" value={formData.locationName} />
            <p className={"text-gray-500 mb-0 text-sm"}>Travel Dates</p>
            <TextBlock
              label="Start of Service"
              value={formatDate(formData.startDate, "", true)}
            />
            <TextBlock
              label="End of Service"
              value={formatDate(formData.endDate, "", true)}
            />
          </div>
          <div>
            <TextBlock
              label="Quotation Date"
              value={formatDate(formData.quotationDate, "", true)}
            />
            <TextBlock label="Package Type" value={formData.basePackageName} />
          </div>
        </section>
        <section>
          <SummaryTable formData={formData} downloading={downloading} />
        </section>
        <section>
          <div className="w-full text-[0.8em] border border-gray-400">
            <div>
              <div className="flex bg-gray-900 text-white px-4">
                <div className="py-1 text-left font-bold">
                  PACKAGE INCLUSIONS
                </div>
              </div>
            </div>
            <div className="bg-white px-4 py-2 font-semibold">
              <ul className="mb-2 space-y-2">
                {packageInclusions?.map((item, index) => (
                  <li key={index} className="relative">
                    <div>
                      {downloading ? (
                        <p className="font-normal text-black">
                          {getValues(`packageInclusions.${index}.value`)}
                        </p>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <PreviewInputControl
                            control={control}
                            name={`packageInclusions.${index}.value`}
                            placeholder="Add Notes"
                            className="text-black font-normal w-full h-full py-1"
                            containerClassName="flex-1"
                          />
                          <button
                            onClick={() => remove(index)}
                            className="absolute -right-7 bg-red-500 text-white hover:bg-red-600 p-1 pi pi-times rounded-full"
                          ></button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {!downloading && (
                <button
                  className="text-gray-600 w-full py-2 hover:bg-gray-100 border border-gray-200 pi pi-plus"
                  onClick={() => append({ value: "" })}
                ></button>
              )}
            </div>
          </div>
        </section>
        <section>
          <div className="w-full text-[0.8em] border border-gray-400">
            <div>
              <div className="bg-gray-900 text-white">
                <div className="px-4 py-1 text-left font-bold">ITINERARY</div>
              </div>
            </div>
            <div className="pb-4 px-4">
              {dayOptions.map((item, index) => (
                <div key={index} className="bg-white flex flex-col">
                  <div className="flex">
                    <div className="py-2 font-semibold align-top w-1/6">
                      {/* item?.label */}
                      {downloading ? (
                        <h6 className="px-1">{customDays?.[index]?.label || item?.label}</h6>
                      ) : (
                        <PreviewInputControl
                          className="w-full placeholder-black pb-2"
                          control={control}
                          placeholder={item?.label}
                          name={`customDays.${index}.label`}
                        />
                      )}
                    </div>
                    <div className="px-4 py-2 font-semibold flex-1 w-full">
                      {formData?.activities?.map((activity, actIndex) => (
                        <div key={actIndex}>
                          {activity?.day === item.value && (
                            <div>
                              {downloading ? (
                                <>
                                  <h6 className="px-1">{activity?.name}</h6>
                                  <p className="font-normal px-1">
                                    {getValues(
                                      `activities.${actIndex}.description`,
                                    )}
                                  </p>
                                </>
                              ) : (
                                <>
                                  <PreviewInputControl
                                    className="font-bold w-full"
                                    control={control}
                                    name={`activities.${actIndex}.name`}
                                  />
                                  <PreviewTextAreaControl
                                    className="font-normal w-full"
                                    control={control}
                                    name={`activities.${actIndex}.description`}
                                  />
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  {customRows?.map((rowItem, rowIndex) => {
                    if (rowItem.display_location === `day${index}`) {
                      return (
                        <div className="pb-2">
                          {downloading ? (
                            <p className="font-normal text-black">
                              {getValues(`customRows.${rowIndex}.value`)}
                            </p>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <PreviewInputControl
                                control={control}
                                name={`customRows.${rowIndex}.value`}
                                placeholder="Add Notes"
                                className="text-black font-normal w-full h-full py-1"
                                containerClassName="flex-1"
                              />
                              <button
                                onClick={() => removeRow(rowIndex)}
                                className="absolute right-[4.6em] bg-red-500 text-white hover:bg-red-600 p-1 pi pi-times rounded-full"
                              ></button>
                            </div>
                          )}
                        </div>
                      );
                    }
                  })}
                  {!downloading && (
                    <button
                      className="text-gray-600 w-full py-2 hover:bg-gray-100 border border-gray-200 pi pi-plus"
                      onClick={() =>
                        appendRow({
                          display_location: `day${index}`,
                          value: "",
                          col_one: "",
                          col_two: "",
                          col_three: "",
                          col_four: "",
                        })
                      }
                    ></button>
  )}
                </div>
              ))}
            </div>
          </div>
        </section>
        <section>
          <p className="font-bold text-[0.8em] mt-5">OPTIONAL:</p>
          <textarea
            className="text-[0.8em] text-orange-400 p-1 font-semibold w-full placeholder-orange-400"
            spellCheck={false}
            defaultValue={optionalActivities
              .map((activity) => {
                return `${activity.name}`;
              })
              .join("\n")}
          />
        </section>
      </div>
      <div>
        <img
          id="preview-footer"
          src={appAsset.previewFooter}
          alt="Preview Footer"
          className="w-full py-2 bg-[#262626]"
        />
      </div>
    </div>
  );
}

export default PreviewFile;
